import "./duration.scss";
import React from "react";
import moment, { type Moment } from "moment";
import {
    formatDate,
    formatDateTime,
    formatTime,
    uppercaseFirst,
} from "../../Utilities/Generic/Strings";

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLSpanElement>, HTMLSpanElement> {
    timestamp: string | Moment;
    date?: boolean;
    time?: boolean;
    suffix?: boolean;
    uppercase?: boolean;
    disabled?: boolean;
}

type State = {
    print: string;
    mode: "duration" | "timestamp";
};

export default class Duration extends React.Component<Props, State> {
    public static defaultProps = {
        date: true,
        time: true,
    };

    public interval: ReturnType<typeof setInterval> | undefined;

    constructor(props: Readonly<Props>) {
        super(props);

        let print = moment(props.timestamp).fromNow(props.suffix);

        if(props.uppercase) {
            print = uppercaseFirst(print);
        }

        this.state = {
            mode: "duration",
            print,
        };
    }

    public componentDidMount(): void {
        if(!this.props.disabled) {
            this.interval = setInterval(this.updatePrint, 1000);
        }
    }

    public componentWillUnmount(): void {
        if(this.interval) {
            clearInterval(this.interval);
        }
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if(prevState.mode !== this.state.mode || prevProps.disabled !== this.props.disabled) {
            if(!this.props.disabled) {
                this.updatePrint();
            }

            if(this.state.mode === "duration" && !this.props.disabled) {
                this.interval = setInterval(this.updatePrint, 1000);
            } else if(this.interval && (this.props.disabled || this.state.mode === "timestamp")) {
                clearInterval(this.interval);
            }
        }
    }

    public updatePrint = (): void => {
        if(this.state.mode === "duration") {
            let print = moment(this.props.timestamp).fromNow(this.props.suffix);

            if(this.props.uppercase) {
                print = uppercaseFirst(print);
            }

            this.setState({
                print,
            });
        } else if(this.state.mode === "timestamp") {
            let formatFunction = formatDateTime;

            if(this.props.time && !this.props.date) {
                formatFunction = formatTime;
            } else if(!this.props.time && this.props.date) {
                formatFunction = formatDate;
            }

            this.setState({
                print: formatFunction(this.props.timestamp),
            });
        }
    };

    public onClick = (e: React.MouseEvent<HTMLSpanElement>): void => {
        e.stopPropagation();

        this.setState(prevState => ({
            mode: (prevState.mode === "duration" ? "timestamp" : "duration"),
        }));
    };

    public render(): React.ReactNode {
        const { className, time, date, suffix, uppercase, timestamp, ...props } = this.props;

        return (
            <span
                {...props}
                onClick={this.onClick}
                className={"duration" + (className ? ` ${className}` : "")}
            >
                {this.state.print}
            </span>
        );
    }
}
